import { memo, useCallback, useMemo } from "react";

import { Localized } from "@fluent/react";
import {
  Row,
  Button,
  formatNumber,
  Popover,
  IconButton,
} from "@narmi/design_system";
import cc from "classcat";

import ManageExternalAccountsPopoverContent from "../ManageExternalAccountsPopoverContent/ManageExternalAccountsPopoverContent";
import { useExternalAccountsStorage } from "../storage";
import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import { createFNVHashSync } from "../../../../../components/authentication/utils";

import styles from "./ExternalAccountsTopRow.module.scss";
import NoLinkedAccounts from "./NoLinkedAccounts";

import type Account from "byzantine/src/Account";

const ExternalAccountsTopRow = ({
  externalAccounts,
  hiddenHashedAccountIdsSet,
  onLinkAccount,
  onClose,
}: {
  externalAccounts: Account[];
  hiddenHashedAccountIdsSet: Set<string>;
  onLinkAccount: () => void;
  onClose: () => void;
}) => {
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";

  const [{ sortedBy }, setExternalAccountsState] =
    useExternalAccountsStorage(userId);

  const externalAccountLocalMetadata = useMemo(
    () =>
      externalAccounts.map((account) => ({
        id: account.id,
        hashedId: createFNVHashSync(account.id),
        isUnsynced:
          account.synced_with === null ||
          account.sync_status === null ||
          account.sync_status === "disconnected",
        availableBalance: account.availableBalanceAsFloat(),
      })),
    [externalAccounts]
  );

  const { checkedAccountIds, unsyncedAccountsCount } = useMemo(
    () =>
      externalAccountLocalMetadata.reduce(
        (acc, account) => {
          if (hiddenHashedAccountIdsSet.has(account.hashedId)) {
            return acc;
          }

          acc.checkedAccountIds.add(account.id);

          if (account.isUnsynced) {
            return {
              ...acc,
              unsyncedAccountsCount: acc.unsyncedAccountsCount + 1,
            };
          }

          return acc;
        },
        {
          checkedAccountIds: new Set<string>(),
          unsyncedAccountsCount: 0,
        }
      ),
    [externalAccountLocalMetadata, hiddenHashedAccountIdsSet]
  );

  const resetHiddenHashedAccountIds = useCallback(() => {
    setExternalAccountsState((prev) => {
      return { ...prev, hiddenHashedAccountIds: [] };
    });
  }, [setExternalAccountsState]);

  const isAllCheckedExternalAccountsUnsynced =
    checkedAccountIds.size > 0 &&
    checkedAccountIds.size === unsyncedAccountsCount;

  const isBalanceHidden =
    checkedAccountIds.size === 0 || isAllCheckedExternalAccountsUnsynced;

  const totalBalance = useMemo(
    () =>
      externalAccountLocalMetadata.reduce((total, account) => {
        if (
          hiddenHashedAccountIdsSet.has(account.hashedId) ||
          account.isUnsynced
        ) {
          return total;
        }

        return total + account.availableBalance;
      }, 0),
    [externalAccountLocalMetadata, hiddenHashedAccountIdsSet]
  );

  const navigateToLinkedAccountsPage = useCallback(() => {
    window.location.assign("/settings/linked_accounts");
  }, []);

  const isExternalAccountsEmpty = externalAccounts.length === 0;

  const isShowExternalAccountsButtonHidden =
    isExternalAccountsEmpty || checkedAccountIds.size > 0;

  return (
    <Row
      justifyContent="space-between"
      gapSize="none"
      alignItems={!isExternalAccountsEmpty ? "center" : undefined}
      className={cc([
        styles.containerHeadingRow,
        checkedAccountIds.size > 0 ? "margin--bottom--s" : "",
        isExternalAccountsEmpty ? styles.containerHeadingRowEmpty : "",
      ])}
    >
      <div
        className={cc([
          styles.externalAccountsHeadingContainer,
          isExternalAccountsEmpty &&
            styles.externalAccountsHeadingContainerHidden,
        ])}
      >
        <Row alignItems="center" gapSize="xxs">
          <h4
            className={styles.header}
            onClick={navigateToLinkedAccountsPage}
            data-testid="external-accounts-header"
          >
            <Localized id="external-accounts-container-header">
              External Accounts
            </Localized>
          </h4>
          <span
            onClick={navigateToLinkedAccountsPage}
            data-testid="external-accounts-chevron"
            className={cc(["narmi-icon-chevron-right", styles["chevron-icon"]])}
          />
        </Row>
      </div>

      <NoLinkedAccounts
        isVisible={isExternalAccountsEmpty}
        onLinkAccount={onLinkAccount}
        onClose={onClose}
      />

      <div
        className={cc([
          styles.details,
          isExternalAccountsEmpty && styles.detailsHidden,
        ])}
      >
        <div
          aria-hidden={checkedAccountIds.size === 0}
          className={cc([
            styles.iconBalanceSwap,
            checkedAccountIds.size === 0
              ? styles.iconBalanceSwapHidden
              : isBalanceHidden && styles.iconBalanceSwapNoBalance,
          ])}
        >
          <span
            className={styles.totalBalance}
            data-testid="external-accounts-total-balance"
            aria-hidden={isBalanceHidden}
          >
            {formatNumber(totalBalance, "currency")}
          </span>

          <Popover
            content={
              <ManageExternalAccountsPopoverContent
                externalAccounts={externalAccounts}
                currentlySortingBy={sortedBy}
                checkedAccountIds={checkedAccountIds}
                onSortSelection={(selection) =>
                  setExternalAccountsState((prev) => {
                    return { ...prev, sortedBy: selection };
                  })
                }
                onAccountIdSelection={(accountId: string) =>
                  setExternalAccountsState((prev) => {
                    const hashedAccountId = createFNVHashSync(accountId);
                    const nextHiddenAccountIds = new Set(
                      prev.hiddenHashedAccountIds
                    );

                    if (nextHiddenAccountIds.has(hashedAccountId)) {
                      nextHiddenAccountIds.delete(hashedAccountId);
                    } else {
                      nextHiddenAccountIds.add(hashedAccountId);
                    }

                    return {
                      ...prev,
                      hiddenHashedAccountIds: [...nextHiddenAccountIds],
                    };
                  })
                }
              />
            }
          >
            <div className={styles.iconButton}>
              <IconButton
                kind="action"
                name="more-vertical"
                data-testid="external-accounts-manage-button"
              />
            </div>
          </Popover>
        </div>
        <div
          className={cc([
            styles.showExternalAccounts,
            isShowExternalAccountsButtonHidden &&
              styles.showExternalAccountsHidden,
          ])}
          aria-hidden={isShowExternalAccountsButtonHidden}
          data-testid="show-external-accounts-container"
        >
          <Button kind="plain" onClick={resetHiddenHashedAccountIds}>
            <Localized id="external-accounts-show-button">
              Show external accounts
            </Localized>
          </Button>
        </div>
      </div>
    </Row>
  );
};

export default memo(ExternalAccountsTopRow);
